var __runInitializers = (this && this.__runInitializers) || function (thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
};
var __esDecorate = (this && this.__esDecorate) || function (ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
};
import { withErrno } from 'kerium';
import { _throw, memoize } from 'utilium';
import { sizeof } from './misc.js';
import * as primitives from './primitives.js';
import { isArrayType, isType } from './types.js';
let _strictIndexes = false;
export function useStrictArrayIndexes() {
    _strictIndexes = true;
}
/**
 * The view on memory used for non-primitive array types.
 * This is a *value*
 */
export function StructArray(type, __length) {
    class StructArray extends DataView {
        length;
        type = type;
        *[Symbol.iterator]() {
            for (let i = 0; i < this.length; i++)
                yield this[i];
        }
        _offsets = [0];
        offsetOf(index) {
            if (!type.isDynamic)
                return index * type.size;
            if (index < this._offsets.length)
                return this._offsets[index];
            for (let i = this._offsets.length; i <= index; i++) {
                this._offsets[i] =
                    this._offsets[i - 1]
                        + sizeof(type.get(this.buffer, this.byteOffset + this._offsets[i - 1]));
            }
            return this._offsets[index];
        }
        constructor(lengthOrBuffer, byteOffset, byteLength) {
            const buffer = typeof lengthOrBuffer === 'object'
                ? lengthOrBuffer
                : new ArrayBuffer((lengthOrBuffer ?? 0) * type.size);
            super(buffer, byteOffset, byteLength);
            this.length =
                typeof lengthOrBuffer === 'number'
                    ? lengthOrBuffer
                    : type.isDynamic
                        ? (__length ?? _throw(`Unknown length of StructArray<${type.name}>`))
                        : Math.floor(this.byteLength / type.size);
            const offset = (i) => this.byteOffset + this.offsetOf(i);
            const views = _isStructConstructor(type) && !type.isDynamic ? [] : undefined;
            return new Proxy(this, {
                get(target, index) {
                    if (index in target)
                        return target[index];
                    const i = parseInt(index.toString());
                    if (!Number.isSafeInteger(i))
                        if (_strictIndexes)
                            throw withErrno('EINVAL', 'Invalid index: ' + index.toString());
                        else
                            return undefined;
                    if (!views)
                        return type.get(target.buffer, offset(i));
                    return (views[i] ??= type.get(target.buffer, offset(i)));
                },
                set(target, index, value) {
                    const i = parseInt(index.toString());
                    if (!Number.isSafeInteger(i))
                        if (_strictIndexes)
                            throw withErrno('EINVAL', 'Invalid index: ' + index.toString());
                        else
                            return false;
                    type.set(target.buffer, offset(i), value);
                    return true;
                },
            });
        }
    }
    for (const key of Object.getOwnPropertyNames(DataView.prototype)) {
        if (!key.startsWith('get') && !key.startsWith('set'))
            continue;
        Object.defineProperty(StructArray.prototype, key, {
            enumerable: false,
            configurable: false,
            writable: false,
            value: undefined,
        });
    }
    return StructArray;
}
function _isStructConstructor(arg) {
    return (typeof arg == 'function'
        && 'prototype' in arg
        && 'fields' in arg
        && typeof arg.fields == 'object'
        && isType(arg));
}
/**
 * A class used to create any *type* representing an array of a given "inner" or element type.
 */
let ArrayType = (() => {
    let _instanceExtraInitializers = [];
    let _get___structArray_decorators;
    let _get___arrayType_decorators;
    let _get_array_decorators;
    return class ArrayType {
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            _get___structArray_decorators = [memoize];
            _get___arrayType_decorators = [memoize];
            _get_array_decorators = [memoize];
            __esDecorate(this, null, _get___structArray_decorators, { kind: "getter", name: "__structArray", static: false, private: false, access: { has: obj => "__structArray" in obj, get: obj => obj.__structArray }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get___arrayType_decorators, { kind: "getter", name: "__arrayType", static: false, private: false, access: { has: obj => "__arrayType" in obj, get: obj => obj.__arrayType }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_array_decorators, { kind: "getter", name: "array", static: false, private: false, access: { has: obj => "array" in obj, get: obj => obj.array }, metadata: _metadata }, null, _instanceExtraInitializers);
            if (_metadata) Object.defineProperty(this, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        }
        type = __runInitializers(this, _instanceExtraInitializers);
        length;
        name;
        size;
        // Defining a `StructArray` class is expensive, and most array types never need one
        get __structArray() {
            return StructArray(this.type, this.length);
        }
        get __arrayType() {
            return this.type.array ? this.type.array : this.__structArray;
        }
        /** @internal @hidden */
        __isArrayType = true;
        /**
         * The "root" type of the array.
         * For example, `uint8` for `uint8[x][y]`
         */
        baseType;
        /**
         * Use `ArrayType.for()` instead!
         * @todo [breaking] make this protected
         * @internal
         */
        constructor(type, length) {
            this.type = type;
            this.length = length;
            this.name = `${type.name}[${length}]`;
            this.size = type.size * length;
            this.baseType = type;
            while (isArrayType(this.baseType))
                this.baseType = this.baseType.type;
        }
        /** Array types are immutable and fully described by their name, so they can be shared. */
        static _cache = new Map();
        /** Get the array type for an element type and length, reusing an existing one when possible. */
        static for(type, length) {
            const name = `${type.name}[${length}]`;
            // @todo replace with `return this._cache.getOrInsertComputed(name, () => new ArrayType(type, length));` once `getOrInsertComputed` is more widespread
            let arrayType = this._cache.get(name);
            if (!arrayType)
                this._cache.set(name, (arrayType = new ArrayType(type, length)));
            return arrayType;
        }
        get = (buffer, offset) => {
            if (primitives.isValid(this.type.name) && offset % this.type.size !== 0) {
                return new this.__structArray(buffer, offset, this.size);
            }
            return new this.__arrayType(buffer, offset, this.size);
        };
        set = (buffer, offset, value) => {
            if (this.length)
                for (let i = 0; i < this.length; i++) {
                    this.type.set(buffer, offset + i * this.type.size, value[i]);
                }
            else {
                let pointer = offset;
                for (let i = 0; i < value.length; i++) {
                    this.type.set(buffer, pointer, value[i]);
                    pointer +=
                        _isStructConstructor(this.type) && this.type.isDynamic
                            ? sizeof(value[i])
                            : this.type.size;
                }
            }
        };
        /**
         * This is for an array of this array
         */
        get array() {
            return StructArray(this);
        }
    };
})();
export { ArrayType };
